import { useEffect, useState } from 'react';
import {
  subscribeToPushBlockedModal,
  closePushBlockedModal,
} from '../../services/pushBlockedModalService';
import { isAndroid, isIOS, isStandalonePWA } from '../../services/platformService';
import { Modal } from './Modal';

/**
 * Platform-specific copy for unblocking push notifications via OS/browser
 * settings. No in-app re-enable is possible — once a browser permission
 * is `denied`, every browser requires the user to flip it back manually.
 */
function deniedInstructionsText(): string {
  if (isIOS() && isStandalonePWA()) {
    return 'Open iPhone Settings → HomePlated → Notifications → toggle Allow Notifications on.';
  }
  if (isAndroid()) {
    return 'Tap the lock icon in your browser address bar → Site settings → Notifications → Allow.';
  }
  // Desktop fallback covers Chrome / Edge / Brave / Safari / Firefox.
  return 'In your browser address bar, click the lock icon → Site settings → allow Notifications.';
}

/**
 * Small modal surfaced when the user taps the Push toggle ON in Settings
 * while OS push permission is denied. Communicates what they need to do
 * in OS/browser settings and offers a single Close action.
 *
 * Intentionally minimal — the row in Settings used to live permanently to
 * communicate this, but that was a constant reminder for users who had
 * deliberately declined. Now we surface the instructions only at the
 * moment the user expresses intent (tries to turn push on) and then get
 * out of the way.
 */
export const PushBlockedModal: React.FC = () => {
  const [state, setState] = useState({ isOpen: false });

  useEffect(() => {
    return subscribeToPushBlockedModal(setState);
  }, []);

  return (
    <Modal isOpen={state.isOpen} onClose={closePushBlockedModal} size="md" zIndexClass="z-50">
      <h2 className="text-lg font-bold text-foreground mb-2">
        Push notifications are blocked
      </h2>
      <p className="text-sm text-muted-foreground mb-4">
        {deniedInstructionsText()}
      </p>
      <div className="flex justify-end">
        <button
          onClick={closePushBlockedModal}
          className="px-4 py-2 text-sm font-medium text-white bg-brand-primary rounded-lg hover:opacity-90 transition-colors"
        >
          Close
        </button>
      </div>
    </Modal>
  );
};
